import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, HardDrive, FlaskConical, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FolderLayoutTab } from '../components/drive/FolderLayoutTab';
import { DocumentTypesTab } from '../components/drive/DocumentTypesTab';
import { TreePreviewTab } from '../components/drive/TreePreviewTab';
import { DriveLogTab } from '../components/drive/DriveLogTab';
import { CleanupTab } from '../components/drive/CleanupTab';

const StatusPill = ({ o, onConnect }) => {
  if (o.connected) return <span data-testid="drive-status-pill" className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><HardDrive size={12} /> Connected · {o.connected_email}</span>;
  if (o.simulated) return <span data-testid="drive-status-pill" className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"><FlaskConical size={12} /> TEST MODE · simulated Drive</span>;
  return <button data-testid="drive-status-pill" onClick={onConnect} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"><AlertTriangle size={12} /> Not connected · connect Drive</button>;
};

export default function DocumentsDriveAdminPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [rev, setRev] = useState(0);
  const load = useCallback(() => api.get('/drive/overview').then((r) => { setO(r.data); setRev((n) => n + 1); }).catch(() => toast.error('Could not load Drive settings')), [api]);
  useEffect(() => { load(); }, [load]);

  if (!o) return <div className="flex items-center justify-center h-64 text-slate-500" data-testid="documents-drive-loading"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;
  const props = { api, overview: o, onSaved: load, toast };
  return (
    <div className="space-y-4" data-testid="documents-drive-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Documents & Drive</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">One set of rules for every PDF and upload — runner sends, manual saves, emails and uploads all use the same folder and file names.</p>
        </div>
        <StatusPill o={o} onConnect={() => navigate('/admin/google-drive')} />
      </div>
      <Tabs defaultValue="layout">
        <TabsList data-testid="documents-drive-tabs">
          <TabsTrigger value="layout" data-testid="tab-folder-layout">Folder Layout</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-document-types">Document Types</TabsTrigger>
          <TabsTrigger value="tree" data-testid="tab-tree-preview">Live Tree Preview</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-drive-log">Drive Log</TabsTrigger>
          <TabsTrigger value="cleanup" data-testid="tab-cleanup">Cleanup</TabsTrigger>
        </TabsList>
        <TabsContent value="layout"><FolderLayoutTab key={`l${rev}`} {...props} /></TabsContent>
        <TabsContent value="docs"><DocumentTypesTab key={`d${rev}`} {...props} /></TabsContent>
        <TabsContent value="tree"><TreePreviewTab api={api} toast={toast} /></TabsContent>
        <TabsContent value="log"><DriveLogTab api={api} /></TabsContent>
        <TabsContent value="cleanup"><CleanupTab api={api} toast={toast} /></TabsContent>
      </Tabs>
    </div>
  );
}
